import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, User } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const location = useLocation();
  const [isLoggedIn, setIsLoggedIn] = React.useState(false);

  React.useEffect(() => {
    const user = localStorage.getItem('tekstar_user');
    setIsLoggedIn(!!user);
  }, [location]);

  const handleLogout = () => {
    localStorage.removeItem('tekstar_user');
    localStorage.removeItem('tekstar_token');
    setIsLoggedIn(false);
    window.location.href = '/';
  };

  return (
    <nav className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <div className="text-2xl font-bold text-pink-600">Tekstar</div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-gray-700 hover:text-gray-900 transition-colors">
              Ride
            </Link>
            <Link to="/drive" className="text-gray-700 hover:text-gray-900 transition-colors">
              Drive
            </Link>
            <Link to="/biker/login" className="text-gray-700 hover:text-gray-900 transition-colors">
              Bike
            </Link>
            <Link to="/bus" data-testid="nav-bus-link" className="text-gray-700 hover:text-gray-900 transition-colors">
              Bus
            </Link>
            <Link to="/courier" data-testid="nav-courier-link" className="text-gray-700 hover:text-gray-900 transition-colors">
              Courier
            </Link>
            <Link to="/business" className="text-gray-700 hover:text-gray-900 transition-colors">
              Business
            </Link>
            {isLoggedIn && (
              <>
                <Link to="/rides" className="text-gray-700 hover:text-gray-900 transition-colors">
                  My Rides
                </Link>
                <Link to="/deliveries" className="text-gray-700 hover:text-gray-900 transition-colors">
                  Deliveries
                </Link>
                <Link to="/multi-drop" className="text-gray-700 hover:text-gray-900 transition-colors">
                  Multi-Drop
                </Link>
                <Link to="/multi-drop/mine" className="text-gray-700 hover:text-gray-900 transition-colors">
                  My Drops
                </Link>
                <Link to="/scheduled-rides" className="text-gray-700 hover:text-gray-900 transition-colors">
                  Scheduled
                </Link>
                <Link to="/recurring-rides" className="text-gray-700 hover:text-gray-900 transition-colors">
                  Commute
                </Link>
              </>
            )}
          </div>

          {/* Desktop Auth Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            {isLoggedIn ? (
              <>
                <Link to="/profile">
                  <Button variant="ghost" size="sm" className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Profile
                  </Button>
                </Link>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleLogout}
                >
                  Log out
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm">Log in</Button>
                </Link>
                <Link to="/signup">
                  <Button 
                    size="sm"
                    className="bg-pink-600 hover:bg-pink-700 text-white"
                  >
                    Sign up
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          <div className="px-4 py-4 space-y-3">
            <Link 
              to="/" 
              className="block py-2 text-gray-700 hover:text-gray-900"
              onClick={() => setIsOpen(false)}
            >
              Ride
            </Link>
            <Link 
              to="/drive" 
              className="block py-2 text-gray-700 hover:text-gray-900"
              onClick={() => setIsOpen(false)}
            >
              Drive
            </Link>
            <Link 
              to="/biker/login" 
              className="block py-2 text-gray-700 hover:text-gray-900"
              onClick={() => setIsOpen(false)}
            >
              Bike
            </Link>
            <Link 
              to="/bus" 
              data-testid="nav-bus-link-mobile"
              className="block py-2 text-gray-700 hover:text-gray-900"
              onClick={() => setIsOpen(false)}
            >
              Bus
            </Link>
            <Link 
              to="/business" 
              className="block py-2 text-gray-700 hover:text-gray-900"
              onClick={() => setIsOpen(false)}
            >
              Business
            </Link>
            {isLoggedIn && (
              <>
                <Link 
                  to="/rides" 
                  className="block py-2 text-gray-700 hover:text-gray-900"
                  onClick={() => setIsOpen(false)}
                >
                  My Rides
                </Link>
                <Link 
                  to="/scheduled-rides" 
                  className="block py-2 text-gray-700 hover:text-gray-900"
                  onClick={() => setIsOpen(false)}
                >
                  Scheduled Rides
                </Link>
              </>
            )}
            <div className="pt-4 space-y-2">
              {isLoggedIn ? (
                <>
                  <Link to="/profile" onClick={() => setIsOpen(false)}>
                    <Button variant="outline" className="w-full" size="sm">
                      Profile
                    </Button>
                  </Link>
                  <Button 
                    variant="outline" 
                    className="w-full" 
                    size="sm"
                    onClick={() => {
                      handleLogout();
                      setIsOpen(false);
                    }}
                  >
                    Log out
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setIsOpen(false)}>
                    <Button variant="outline" className="w-full" size="sm">
                      Log in
                    </Button>
                  </Link>
                  <Link to="/signup" onClick={() => setIsOpen(false)}>
                    <Button 
                      className="w-full bg-pink-600 hover:bg-pink-700 text-white" 
                      size="sm"
                    >
                      Sign up
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;